import { z } from 'zod'
import { LOCALES } from './i18n.js'

/**
 * Where visitors come from. The page sends what the browser knows about the
 * visit (referrer, campaign tags); the server turns it into a source. No
 * cookie, no IP address and no persistent identifier: `visitId` is random and
 * lives as long as the browser tab.
 */

export const ANALYTICS_EVENT_TYPES = ['visit', 'signup'] as const
export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number]

export const AnalyticsEventSchema = z.object({
  type: z.enum(ANALYTICS_EVENT_TYPES),
  /** Random id of the visit (one per browser tab session). */
  visitId: z.string().min(8).max(64),
  /** `document.referrer` of the landing page. Only its host is kept. */
  referrer: z.string().max(300).optional(),
  /** Path the visit started on. */
  landing: z.string().max(200).optional(),
  utmSource: z.string().max(80).optional(),
  utmMedium: z.string().max(80).optional(),
  utmCampaign: z.string().max(120).optional(),
  /** Click identifiers present in the landing URL (they tell the network, nothing else is kept). */
  clickId: z.enum(['fbclid', 'igshid', 'gclid']).optional(),
  locale: z.enum(LOCALES).optional(),
  viewport: z.enum(['mobile', 'desktop']).optional(),
})
export type AnalyticsEventInput = z.infer<typeof AnalyticsEventSchema>

export type TrafficMedium = 'direct' | 'social' | 'search' | 'paid' | 'referral' | 'email' | 'other'

/** What the server keeps for one event. */
export interface AnalyticsEventRecord {
  id: string
  createdAt: string
  type: AnalyticsEventType
  visitId: string
  /** Normalized origin of the visit: `instagram`, `google`, `direct`, a host name… */
  source: string
  medium: TrafficMedium
  campaign: string | null
  referrerHost: string | null
  landing: string | null
  locale: string | null
  viewport: string | null
}

export interface TrafficRow {
  source: string
  medium: TrafficMedium
  visits: number
  signups: number
}

export interface TrafficSummary {
  days: number
  since: string
  totals: { visits: number; signups: number }
  /** Most visited first. */
  sources: TrafficRow[]
  campaigns: (TrafficRow & { campaign: string })[]
  referrers: { host: string; visits: number }[]
  landings: { path: string; visits: number }[]
  daily: { date: string; visits: number; signups: number }[]
}
